import React, { createContext, useEffect, useState } from 'react'
import useProductId from 'modules/react/loan-calculator/hooks/useProductId'
import useProductData from 'modules/react/loan-calculator/hooks/useProductData'
import useLoanCalculation from 'modules/react/loan-calculator/hooks/useLoanCalculation'
import axios from 'axios'
import useFormat from 'modules/react/loan-calculator/hooks/useFormat'

const LoanCalculatorContext = createContext({})

const applicantObj = { firstName: '', lastName: '', phone: '', email: '' }

export default function LoanCalculatorContainer({ emailRoute, carInfo, accessToken, companyId, modalText, webhookUrl, dealerInfo, carImage, tooltips, apiUrl, children }) {
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true)
  const [isLoadingApply, setIsLoadingApply] = useState(false)
  const [step, setStep] = useState(1)
  const [downPayment, setDownPayment] = useState(0)
  const [loanDurationInMonths, setLoanDurationInMonths] = useState(null)
  const [applicant, setApplicant] = useState(applicantObj)
  const [loanCalculation, setLoanCalculation] = useState(null)
  const [interestTypeValue, setInterestTypeValue] = useState('')
  const [marketingIsChecked, setMarketingIsChecked] = useState(false)
  // Fetch data
  const productId = useProductId(apiUrl, companyId, accessToken)
  const {
    productData,
    productDataIsLoading,
    productDataError
  } = useProductData(apiUrl, companyId, accessToken, productId, carInfo, 'AssetId', 'testvehicle')
  const {
    loanCalculationData,
    loanCalculationIsLoading,
    loanCalculationError
  } = useLoanCalculation(apiUrl, companyId, accessToken, carInfo, productId, productData, downPayment, loanDurationInMonths, interestTypeValue)
  const { formatPrice, formatMonths, formatPercentWithDecimal } = useFormat()

  const setApplicantValue = (name, value) => {
    const newApplicant = { ...applicant }
    newApplicant[name] = value
    setApplicant(newApplicant)
  }

  useEffect(() => {
    if (productData && !loanDurationInMonths) {
      setLoanDurationInMonths(productData.durationInMonthsRangePrivate.defaultValue)
      setInterestTypeValue(productData.interestType)
      setDownPayment(productData?.payoutRange?.defaultValue)
      setIsLoadingInitialData(false)
    }
  }, [productData])

  useEffect(() => {
    if (loanCalculationData) {
      setLoanCalculation(loanCalculationData)
    }
  }, [loanCalculationData])

  const applyNow = () => {
    const axiosConfig = { headers: { 'Content-Type': 'application/json' } }
    const applyData = {
      applicant,
      loanCalculation,
      loanDurationInMonths,
      emailContent: {
        downPayment: formatPrice(downPayment),
        loanDuration: formatMonths(loanDurationInMonths),
        financingAmount: formatPrice(loanCalculationData.financingAmount),
        apr: formatPercentWithDecimal(loanCalculationData.apr),
        interestType: interestTypeValue,
        interestRate: formatPercentWithDecimal(loanCalculationData.interestRate),
        creditCosts: formatPrice(loanCalculationData.creditCosts),
        totalCreditPayments: formatPrice(loanCalculationData.totalCreditPayments),
        totalRepaymentInclUpfrontPayment: formatPrice(loanCalculationData.totalRepaymentInclUpfrontPayment),
        monthlyPrice: formatPrice(loanCalculationData.monthlyPrice),
        carImage: carImage
      }
    }

    setIsLoadingApply(true)

    axios
      .post(emailRoute, applyData, axiosConfig)
      .then(() => setStep(3))
      .catch(error => console.log(error))
      .finally(() => setIsLoadingApply(false))

    if (webhookUrl) {
      const webhookData = { applicant, loanCalculation, carInfo, dealerInfo, marketingIsChecked }

      axios
        .post(webhookUrl, webhookData, axiosConfig)
        .catch(error => console.log(error))
    }
  }

  return (
    <LoanCalculatorContext.Provider value={{
      step,
      nextStep: () => setStep(step + 1),
      previousStep: () => setStep(step - 1),
      applyNow,
      carInfo,
      accessToken,
      companyId,
      modalText,
      tooltips,
      downPayment,
      setDownPayment,
      loanDurationInMonths,
      setLoanDurationInMonths,
      applicant,
      setApplicantValue,
      productDataObject: { productData, productDataIsLoading, productDataError },
      loanCalculationObject: { loanCalculationData, loanCalculationIsLoading, loanCalculationError },
      isLoadingInitialData,
      isLoadingApply,
      interestTypeValue,
      setInterestTypeValue,
      marketingIsChecked,
      setMarketingIsChecked
    }}>
      {children}
    </LoanCalculatorContext.Provider>
  )
}

export { LoanCalculatorContext }
